import { useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import KineticHeading from "@/components/KineticHeading";
import AmbientCanvas from "@/components/AmbientCanvas";
import RenderRealitySlider from "@/components/RenderRealitySlider";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import ProjectCard from "@/components/ProjectCard";
import DownloadProfileButton from "@/components/DownloadProfileButton";
import { WhatsAppIcon } from "@/components/Footer";
import { fetchProjects } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";
import { CITIES, PROCESS_STEPS, SITE, waDefaultLink } from "@/lib/site";

const STATS = [
  { value: "19", label: "Years of turnkey practice" },
  { value: "J.J.", label: "School of Art alumnus" },
  { value: "6", label: "Dedicated execution teams" },
  { value: "7", label: "Cities served" },
];

export default function Home() {
  usePageMeta(
    "Shriya Redkar Interiors — The 3D You Approve Is The Home You Get",
    "19 years of turnkey interior design across Mumbai, Navi Mumbai, Thane, Pune, Delhi, Lucknow & Rishikesh. Every project shown as approved 3D render beside the finished site photo."
  );
  const reduce = useReducedMotion();
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: fetchProjects });
  const featured = projects?.find((p) => p.reality_url) ?? projects?.[0];
  const preview = projects ? projects.slice(0, 3) : [];

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen flex-col justify-end overflow-hidden">
        <motion.div style={reduce ? undefined : { y: bgY }} className="absolute inset-0">
          <img
            src="/images/site/hero.jpg"
            alt="A finished Shriya Redkar Interiors home at dusk"
            className="h-full w-full scale-105 object-cover opacity-75"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/45 to-obsidian/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-transparent to-transparent" />
        </motion.div>
        <AmbientCanvas className="absolute inset-0 h-full w-full" />

        <motion.div style={reduce ? undefined : { opacity: fade }} className="container-x relative z-10 pb-16 pt-40 lg:pb-24">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="eyebrow"
            data-testid="hero-overline"
          >
            Shriya Redkar Interiors — Turnkey Interior Architecture
          </motion.p>

          <KineticHeading
            className="mt-6 font-serif font-light leading-[1.04] text-zinc-50 text-[clamp(2.9rem,8.2vw,7rem)]"
            delay={0.35}
            lines={[
              { text: "Designed in 3D." },
              { text: "Delivered in reality.", className: "italic text-gold" },
            ]}
          />

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base font-light leading-relaxed text-zinc-300 sm:text-lg"
            data-testid="hero-subline"
          >
            For 19 years, homeowners across five cities have walked into homes that match the picture
            they approved. Not close. Not "inspired by." The same.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link to="/enquire" data-testid="hero-cta-enquire-button" className="btn-gold">
              Start your enquiry
            </Link>
            <a href="#proof" data-testid="hero-cta-proof-link" className="btn-ghost">
              See the proof
            </a>
          </motion.div>

          <motion.dl
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-16 grid grid-cols-2 gap-6 border-t border-white/15 pt-8 md:grid-cols-4"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <dt className="font-serif text-3xl text-gold md:text-4xl">{s.value}</dt>
                <dd className="mt-1 text-[11px] uppercase tracking-[0.16em] text-zinc-400">{s.label}</dd>
              </div>
            ))}
          </motion.dl>
        </motion.div>
      </section>

      <Marquee />

      {/* THE PROOF */}
      <section id="proof" className="container-x py-24 lg:py-32">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="eyebrow" data-testid="proof-eyebrow">Render vs. Reality — our signature</p>
              <h2 className="mt-5 font-serif text-4xl font-light leading-[1.08] text-zinc-50 sm:text-5xl lg:text-6xl">
                Approved on screen.
                <span className="block italic text-gold">Delivered on site.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-4">
            <p className="text-base leading-relaxed text-zinc-400">
              Every project on this site shows both pictures: the 3D render you'd have signed off, and the
              photograph of what we handed over. Drag the handle. Ask any competitor to do the same.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-14">
          {featured ? (
            <RenderRealitySlider
              idPrefix="home-proof"
              renderSrc={featured.render_url}
              realitySrc={featured.reality_url}
              renderAlt={`${featured.title} — approved 3D render`}
              realityAlt={`${featured.title} — finished reality photo`}
              materials={featured.materials}
              duration={`${featured.timeline_days} days`}
              city={`${featured.city}, ${featured.year}`}
              budget={featured.budget_label}
              note={featured.honesty}
            />
          ) : (
            <div className="aspect-[16/10] w-full animate-pulse bg-obsidian-card" />
          )}
        </Reveal>

        <Reveal delay={0.15}>
          <Link
            to="/portfolio"
            data-testid="proof-view-portfolio-link"
            className="mt-10 inline-flex items-center gap-3 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-colors hover:text-gold-light"
          >
            See every project, both pictures <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      {/* SELECTED WORK */}
      <section className="container-x py-24 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="eyebrow">Selected work</p>
            <h2 className="mt-5 font-serif text-4xl font-light text-zinc-50 sm:text-5xl">
              Homes that kept their promise.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/portfolio"
              data-testid="work-view-all-link"
              className="btn-ghost !px-6 !py-3"
            >
              View full portfolio
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-12">
          {preview.length > 0 && (
            <>
              <Reveal className="lg:col-span-7">
                <div className="aspect-[16/11] w-full">
                  <ProjectCard project={preview[0]} />
                </div>
              </Reveal>
              <div className="flex flex-col gap-6 lg:col-span-5">
                <Reveal delay={0.1} className="min-h-0 flex-1">
                  <div className="aspect-[16/10] w-full lg:aspect-auto lg:h-full">
                    <ProjectCard project={preview[1]} />
                  </div>
                </Reveal>
                <Reveal delay={0.2} className="min-h-0 flex-1">
                  <div className="aspect-[16/10] w-full lg:aspect-auto lg:h-full">
                    <ProjectCard project={preview[2]} />
                  </div>
                </Reveal>
              </div>
            </>
          )}
        </div>
      </section>

      {/* PROCESS TEASER */}
      <section className="border-y border-white/10 bg-obsidian-soft">
        <div className="container-x py-24 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="eyebrow">The turnkey process</p>
                <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-zinc-50 sm:text-5xl">
                  Five steps between "good idea" and handover.
                </h2>
                <p className="mt-6 max-w-sm text-base leading-relaxed text-zinc-400">
                  The "will it look like the render?" question dies at step two — because your 3D is built
                  from the actual materials, priced before work begins.
                </p>
                <Link to="/process" data-testid="process-teaser-link" className="btn-gold mt-9">
                  Walk the process
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              {PROCESS_STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.06}>
                  <div className="grid grid-cols-12 items-baseline gap-3 border-t border-white/10 py-6" data-testid={`process-step-${s.n}`}>
                    <span className="col-span-2 font-serif text-2xl text-gold/70 md:text-3xl">{s.n}</span>
                    <h3 className="col-span-10 font-serif text-xl text-zinc-100 md:col-span-4 md:text-2xl">{s.title}</h3>
                    <p className="col-span-12 text-sm leading-relaxed text-zinc-400 md:col-span-6">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="container-x py-24 lg:py-32">
        <Reveal>
          <p className="eyebrow">Where we build</p>
          <h2 className="mt-5 max-w-2xl font-serif text-4xl font-light text-zinc-50 sm:text-5xl">
            Six execution teams across five cities.
          </h2>
        </Reveal>
        <div className="mt-12">
          {CITIES.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.05}>
              <Link
                to={`/interiors/${c.slug}`}
                data-testid={`home-link-city-${c.slug}`}
                className="group flex items-baseline justify-between gap-6 border-t border-white/10 py-5 transition-colors last:border-b hover:bg-white/[0.02] sm:py-6"
              >
                <span className="flex items-baseline gap-5 sm:gap-8">
                  <span className="font-mono text-xs text-zinc-600">0{i + 1}</span>
                  <span className="font-serif text-3xl text-zinc-100 transition-colors group-hover:text-gold sm:text-5xl">
                    {c.name}
                  </span>
                </span>
                <span className="hidden max-w-xs text-right text-xs leading-relaxed text-zinc-500 md:block">{c.areas}</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="container-x py-24 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="frame-clipped">
              <img
                src="/images/site/portrait.jpg"
                alt="Shriya Redkar in her studio"
                loading="lazy"
                className="aspect-[3/4] w-full object-cover"
                data-testid="about-teaser-portrait"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="eyebrow">The studio</p>
              <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-zinc-50 sm:text-5xl">
                Nineteen years. One accountable name.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-zinc-400">
                A J.J. School of Art alumnus, Shriya Redkar has spent 19 years doing one thing well:
                full-home turnkey interiors where the render and the site photo are the same picture.
                She still signs off every render personally — and walks every site before handover.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/about" data-testid="about-teaser-link" className="btn-ghost">
                  Meet Shriya
                </Link>
                <DownloadProfileButton testId="home-download-profile" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden">
        <img src="/images/site/flatlay.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/70 to-obsidian" />
        <div className="container-x relative z-10 py-28 text-center lg:py-36">
          <Reveal>
            <p className="eyebrow">The question every client asks</p>
            <h2 className="mx-auto mt-6 max-w-4xl font-serif text-4xl font-light leading-[1.1] text-zinc-50 sm:text-6xl">
              "Will it look like the render?"
              <span className="mt-3 block italic text-gold">Yes. And we prove it every time.</span>
            </h2>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <Link to="/enquire" data-testid="home-final-cta-enquire" className="btn-gold">
                Start your enquiry
              </Link>
              <a
                href={waDefaultLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-final-cta-whatsapp"
                className="btn-ghost"
              >
                <WhatsAppIcon className="h-4 w-4 text-wa" /> WhatsApp {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
